import { useQueryClient } from "@tanstack/react-query";
import {
  useDataProvider,
  useNotify,
  useTranslate,
  type DataProvider,
} from "ra-core";
import { useCallback } from "react";

import type { ProjectAccess, Sale } from "../types";

const RESOURCES = [
  "projects",
  "pipelines",
  "pipeline_stages",
  "sales_projects",
  "sales",
  "deals",
];

/** The database message as it is (it is Russian), with a few known ones made readable. */
export const errorText = (
  error: unknown,
  translate: (key: string) => string,
) => {
  const message =
    (error as { body?: { message?: string } })?.body?.message ??
    (error as Error)?.message ??
    String(error);
  if (/pipeline_stages_name_uq/.test(message)) {
    return translate("crm.projects.errors.stage_exists");
  }
  if (/sales_projects_uq/.test(message)) {
    return translate("crm.projects.errors.member_exists");
  }
  return message;
};

/**
 * Runs a change of Settings → Projects / Employees: shows the database's
 * refusal as it is («Перенесите записи на другую стадию», pipeline rules) and
 * refreshes every list the change may touch (marking a lead stage «won»
 * moves the mark off another stage, renaming a stage renames deals…).
 */
export const useProjectAdmin = () => {
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const translate = useTranslate();

  return useCallback(
    async <T>(
      change: (dataProvider: DataProvider) => Promise<T>,
    ): Promise<T | undefined> => {
      try {
        return await change(dataProvider);
      } catch (error) {
        notify(errorText(error, translate), {
          type: "error",
          messageArgs: { _: errorText(error, translate) },
        });
        return undefined;
      } finally {
        await Promise.all(
          RESOURCES.map((resource) =>
            queryClient.invalidateQueries({ queryKey: [resource] }),
          ),
        );
      }
    },
    [dataProvider, notify, queryClient, translate],
  );
};

/** Role presets when someone is added to a project; the owner changes them by hand. */
export const presetAccess = (role: Sale["role"]): ProjectAccess =>
  role === "head" ? "department" : role === "owner" ? "all" : "own";
